<template>
  <v-dialog
    v-model="dialogVisible"
    max-width="820"
    persistent
    scrollable
    content-class="calendar-import-dialog"
  >
    <v-card>
      <v-card-title class="text-h6 px-4 pt-4">
        <v-icon class="mr-2">mdi-calendar-import</v-icon>
        P6 Calendars
      </v-card-title>

      <v-card-text>
        <div class="text-body-2 text-medium-emphasis mb-4">
          The uploaded P6 file contains {{ plan.length }}
          calendar{{ plan.length !== 1 ? 's' : '' }}. Choose which to import.
          New calendars are created and published; matching ones (by P6 id or
          code) get a new published revision, keeping the previous one as
          history. Imported tasks are re-linked to the resulting calendars.
        </div>

        <v-alert
          v-if="loadError"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-4"
        >
          {{ loadError }}
        </v-alert>

        <div v-if="loadingPlan" class="text-center py-6">
          <v-progress-circular indeterminate color="primary" :size="28" />
          <div class="text-caption mt-2">Loading project calendars…</div>
        </div>

        <template v-else>
          <v-alert
            v-if="showRenameHint"
            v-model="renameHintVisible"
            type="info"
            variant="tonal"
            density="compact"
            closable
            class="mb-4"
          >
            {{ unmatchedExisting.length }} existing P6
            calendar{{ unmatchedExisting.length !== 1 ? 's' : '' }} in this
            project {{ unmatchedExisting.length !== 1 ? 'were' : 'was' }} not
            matched by this file — a calendar may have been renamed in P6. Use
            the Action column to connect a renamed calendar to its existing
            counterpart instead of creating a duplicate.
          </v-alert>

          <v-table density="compact" class="calendars-table">
            <thead>
              <tr>
                <th style="width: 50px;"></th>
                <th class="text-left">Title</th>
                <th class="text-left">Code</th>
                <th class="text-center" style="width: 90px;">Hours/Day</th>
                <th class="text-left">Working time</th>
                <th class="text-center" style="width: 170px;">Action</th>
                <th class="text-center" style="width: 90px;">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(entry, index) in plan" :key="entry.p6Calendar.p6ObjectId || index">
                <td class="text-center">
                  <v-checkbox
                    v-model="included[index]"
                    hide-details
                    density="compact"
                    :disabled="importing || entryStatus(index) === 'success'"
                  />
                </td>
                <td>{{ entry.p6Calendar.title }}</td>
                <td class="text-caption">{{ entry.p6Calendar.code }}</td>
                <td class="text-center">{{ entry.p6Calendar.hoursPerDay }}</td>
                <td class="text-caption">
                  {{ summarize(entry.p6Calendar) }}
                  <div v-if="entry.p6Calendar.assumedWorkWeek" class="text-warning">
                    The P6 export carries no working hours for this calendar; its P6 standard work week,
                    {{ entry.p6Calendar.assumedWorkWeek }}, is assumed. Confirm with the planner.
                  </div>
                </td>
                <td class="text-center">
                  <template v-if="entry.action === 'update' && entry.matchedBy !== 'manual'">
                    <v-chip size="x-small" color="warning" variant="tonal">
                      {{ isRenamed(entry) ? 'New revision (renamed)' : 'New revision' }}
                    </v-chip>
                    <div class="text-caption text-medium-emphasis">
                      {{ entry.matchedBy === 'p6Id' ? 'matched by P6 id' : 'matched by code' }}
                    </div>
                  </template>
                  <v-select
                    v-else-if="unmatchedExisting.length"
                    :model-value="entry.matchedBy === 'manual' ? entry.existingCalendar?.id : null"
                    :items="reconnectItems(index)"
                    density="compact"
                    variant="outlined"
                    hide-details
                    :disabled="importing || entryStatus(index) === 'success'"
                    @update:model-value="onReconnect(entry, $event)"
                  />
                  <v-chip v-else size="x-small" color="success" variant="tonal">
                    Create
                  </v-chip>
                </td>
                <td class="text-center">
                  <v-progress-circular
                    v-if="entryStatus(index) === 'pending'"
                    indeterminate
                    color="primary"
                    :size="18"
                    :width="2"
                  />
                  <v-icon
                    v-else-if="entryStatus(index) === 'success'"
                    color="success"
                    size="small"
                  >mdi-check-circle</v-icon>
                  <v-tooltip v-else-if="entryStatus(index) === 'error'" location="top">
                    <template #activator="{ props: tp }">
                      <v-icon v-bind="tp" color="error" size="small">mdi-alert-circle</v-icon>
                    </template>
                    <span>{{ entryError(index) }}</span>
                  </v-tooltip>
                  <span v-else class="text-grey">—</span>
                </td>
              </tr>
            </tbody>
          </v-table>

          <v-alert
            v-if="hasErrors"
            type="warning"
            variant="tonal"
            density="compact"
            class="mt-4"
          >
            Some calendars failed to import. You can retry the failed ones, or
            continue — tasks using those calendars keep their default calendar.
          </v-alert>
        </template>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-btn variant="text" :disabled="importing" @click="skip">
          Skip
        </v-btn>
        <v-spacer />
        <v-btn
          v-if="finished"
          color="primary"
          variant="flat"
          @click="continueImport"
        >
          Continue
        </v-btn>
        <template v-else>
          <v-btn
            v-if="hasErrors && !importing"
            variant="text"
            class="mr-2"
            @click="continueImport"
          >
            Continue Anyway
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :loading="importing"
            :disabled="loadingPlan || !anyIncluded"
            @click="runImport"
          >
            {{ hasErrors ? 'Retry Failed' : 'Import Calendars' }}
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useAppStore } from '@/stores/app';
import { useSettingsStore } from '@/stores/settings';
import { useScheduleStore } from '@/stores/schedule';
import { CalendarImporter, summarizeIntervals } from '@/utils/CalendarImporter';

const appStore = useAppStore();
const settingsStore = useSettingsStore();
const scheduleStore = useScheduleStore();

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  p6Calendars: {
    type: Array,
    default: () => []
  }
});

// confirmed payload: { calendarsByP6Id: Map }
const emit = defineEmits(['update:modelValue', 'confirmed', 'skipped']);

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

// Created lazily: settings.projectCode arrives via postMessage from the parent
// frame and may not be populated when this component is first set up.
const getImporter = () => new CalendarImporter(appStore.apiClient, settingsStore.settings.projectCode);

const loadingPlan = ref(false);
const loadError = ref('');
const plan = ref([]);
// Current P6-owned project calendars no parsed calendar matched — likely
// renamed in P6; offered as manual re-connect targets in the Action column.
const unmatchedExisting = ref([]);
const renameHintVisible = ref(true);
const included = ref([]);
const importing = ref(false);
const finished = ref(false);
// index -> 'pending' | 'success' | 'error'
const statuses = ref({});
const errors = ref({});
// Accumulated successful imports across (possibly retried) runs.
const calendarsByP6Id = ref(new Map());

const anyIncluded = computed(() => included.value.some(Boolean));
const hasErrors = computed(() => Object.values(statuses.value).some(s => s === 'error'));
const showRenameHint = computed(() =>
  unmatchedExisting.value.length > 0 &&
  plan.value.some(entry => entry.action === 'create' || entry.matchedBy === 'manual')
);

// A p6-id match survives renames, so a differing title means the calendar was
// renamed in P6 and the update will change its title and code.
const isRenamed = (entry) =>
  entry.matchedBy === 'p6Id' &&
  entry.existingCalendar &&
  entry.p6Calendar.title !== entry.existingCalendar.title;

// Re-connect choices for a create row: 'Create new' plus the orphans not
// already claimed by another row's manual selection.
const reconnectItems = (index) => {
  const takenIds = new Set(
    plan.value
      .filter((entry, i) => i !== index && entry.matchedBy === 'manual' && entry.existingCalendar)
      .map(entry => entry.existingCalendar.id)
  );
  return [
    { title: 'Create new', value: null },
    ...unmatchedExisting.value
      .filter(c => !takenIds.has(c.id))
      .map(c => ({ title: `${c.title} (${c.code})`, value: c.id }))
  ];
};

const onReconnect = (entry, calendarId) => {
  const calendar = calendarId
    ? unmatchedExisting.value.find(c => c.id === calendarId) || null
    : null;
  entry.existingCalendar = calendar;
  entry.action = calendar ? 'update' : 'create';
  entry.matchedBy = calendar ? 'manual' : null;
};

const summarize = (p6Calendar) => summarizeIntervals(p6Calendar);
const entryStatus = (index) => statuses.value[index] || null;
const entryError = (index) => {
  const err = errors.value[index];
  return err?.response?.data?.title || err?.message || 'Import failed';
};

const resetState = () => {
  loadError.value = '';
  plan.value = [];
  unmatchedExisting.value = [];
  renameHintVisible.value = true;
  included.value = [];
  importing.value = false;
  finished.value = false;
  statuses.value = {};
  errors.value = {};
  calendarsByP6Id.value = new Map();
};

const loadPlan = async () => {
  resetState();
  loadingPlan.value = true;
  try {
    const { entries, unmatchedExisting: orphans } = await getImporter().buildPlan(props.p6Calendars);
    plan.value = entries;
    unmatchedExisting.value = orphans;
    included.value = plan.value.map(() => true);
  } catch (error) {
    console.error('Error building calendar import plan:', error);
    loadError.value = 'Could not load project calendars. You can skip this step and import tasks with default calendars.';
    plan.value = (props.p6Calendars || []).map(p6Calendar => ({
      p6Calendar,
      existingCalendar: null,
      action: 'create',
      matchedBy: null
    }));
    unmatchedExisting.value = [];
    included.value = plan.value.map(() => false);
  } finally {
    loadingPlan.value = false;
  }
};

// Refresh the calendar store so the newly published revisions are what the
// commit walks and the review grid evaluate against.
const refreshCalendarStore = async () => {
  try {
    const data = await getImporter().fetchPublishedCalendars();
    const publishedCalendars = (data || []).filter(c => c.current && c.isPublished);
    scheduleStore.setCalendars(publishedCalendars);
  } catch (error) {
    console.error('Error refreshing calendars after import:', error);
  }
};

const runImport = async () => {
  importing.value = true;
  const importer = getImporter();
  try {
    // On a retry, only re-run the still-failed/unfinished entries that are included.
    const entriesToRun = plan.value
      .map((entry, index) => ({ entry, index }))
      .filter(({ index }) => included.value[index] && statuses.value[index] !== 'success');

    for (const { index } of entriesToRun) {
      statuses.value = { ...statuses.value, [index]: 'pending' };
    }

    for (const { entry, index } of entriesToRun) {
      try {
        const calendar = await importer.importCalendar(entry, 'P6 import');
        statuses.value = { ...statuses.value, [index]: 'success' };
        if (entry.p6Calendar.p6ObjectId) {
          calendarsByP6Id.value.set(entry.p6Calendar.p6ObjectId, calendar);
        }
      } catch (error) {
        console.error(`Error importing calendar "${entry.p6Calendar.title}":`, error);
        errors.value = { ...errors.value, [index]: error };
        statuses.value = { ...statuses.value, [index]: 'error' };
      }
    }

    await refreshCalendarStore();
    finished.value = !hasErrors.value;
  } finally {
    importing.value = false;
  }
};

const continueImport = () => {
  emit('confirmed', { calendarsByP6Id: calendarsByP6Id.value });
  dialogVisible.value = false;
};

const skip = () => {
  emit('skipped');
  dialogVisible.value = false;
};

// Build the plan whenever the dialog is opened with calendars.
watch(
  () => props.modelValue,
  (open) => {
    if (open && props.p6Calendars.length) {
      loadPlan();
    }
  }
);
</script>

<style>
/* Unscoped: the dialog teleports to the overlay container outside this
   component's DOM. The app runs in an iframe the parent sizes to content, so
   the iframe viewport can be far taller than the visible screen — viewport-
   relative centering/max-height would push the actions off-screen. Anchor to
   the top and cap the dialog height in px so the actions stay reachable.
   The cap goes on the overlay content: Vuetify's scrollable rule
   (.v-dialog--scrollable > .v-overlay__content > .v-card { max-height: 100% })
   outranks a plain `.calendar-import-dialog > .v-card` selector, so a cap on
   the card alone is silently overridden. The card inherits 100% of this. */
.v-dialog > .v-overlay__content.calendar-import-dialog {
  align-self: flex-start;
  margin-top: 24px;
  max-height: min(calc(100vh - 48px), 640px);
}
</style>

<style scoped>
.calendars-table {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 4px;
}

.calendars-table th {
  background-color: rgba(var(--v-theme-surface-variant), 0.4);
  font-weight: 600;
}
</style>
